import { useEffect } from "react";
import { useFormikContext } from "formik";
import { useFlags } from "flagsmith/react";

import useCNPJsFromBpId from "@hooks/useCNPJsFromBpId";

import { ProfileType, PROFILE_OPTIONS } from "../interface";
import useAddCNPJ from "./useAddCNPJ";

interface UserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  companyName?: string;
  CNPJ?: string;
  profile: string;
  attributes: {
    BPID: string;
    INTERNAL_NAME: string;
    CNPJ: { companyName: string; CNPJ: string }[];
  };
}

export function useUserForm() {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<UserFormValues>();

  const {
    bpIdData,
    isLoading,
    isfinished,
    fetchCNPJsFromBPID,
  } = useCNPJsFromBpId();

  const flags = useFlags([
    "is_carrier_type_user",
    "is_cif_driver_enabled",
    "new_driver_bpid",
  ]);

  const featureFlagBPIDDriver = flags.new_driver_bpid?.enabled;

  const isCarrierProfile = values.profile === ProfileType.CARRIER;
  const isDriverProfile = values.profile === ProfileType.DRIVER;
  const isDriverBPIDValid = isDriverProfile && featureFlagBPIDDriver;
  const isGroupCNPJEmpty = !values?.attributes?.CNPJ?.length;
  const isCNPJFieldsDisabled = (isCarrierProfile && !isfinished) || isLoading;

  const profileOptions = PROFILE_OPTIONS.filter((option) => {
    if (
      option.value === ProfileType.CARRIER &&
      !flags.is_carrier_type_user?.enabled
    )
      return false;
    if (
      option.value === ProfileType.CIF_DRIVER &&
      !flags.is_cif_driver_enabled?.enabled
    )
      return false;
    return true;
  });

  const attrErrors =
    typeof errors.attributes === "object" ? errors.attributes : {};

  const validationErrors = [
    Boolean(errors.profile || !values.profile),
    Boolean(errors.firstName || !values.firstName),
    Boolean(errors.lastName || !values.lastName),
    Boolean(errors.email || !values.email),
    isGroupCNPJEmpty,
  ];

  if (isCarrierProfile) {
    validationErrors.push(
      Boolean(attrErrors?.BPID || !values.attributes?.BPID)
    );
    validationErrors.push(
      Boolean(attrErrors?.INTERNAL_NAME || !values.attributes?.INTERNAL_NAME)
    );
  }
  if (isDriverBPIDValid) {
    validationErrors.push(
      Boolean(attrErrors?.BPID || !values.attributes?.BPID)
    );
  }

  const isFormInvalid = validationErrors.some(Boolean);

  const handleChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFieldValue(field, event.target.value);
  };

  const handleBlur = (field: string) => () => setFieldTouched(field, true);

  const handleProfileChange = (selectedValue: unknown) => {
    setFieldValue("profile", selectedValue);
    setFieldValue("attributes.BPID", "");
    setFieldValue("attributes.INTERNAL_NAME", "");
  };

  const handleSearchBPID = () => {
    const clearGroup = () => {
      setFieldValue("attributes.INTERNAL_NAME", "");
    };
    fetchCNPJsFromBPID(values.attributes?.BPID, clearGroup);
  };

  const handleAddCNPJ = useAddCNPJ(values, setFieldValue);

  const handleRemoveCNPJ = (idx: number) => {
    const updated = [...values.attributes.CNPJ];
    updated.splice(idx, 1);
    setFieldValue("attributes.CNPJ", updated);
  };

  useEffect(() => {
    if (bpIdData?.companies?.length) {
      setFieldValue("attributes.INTERNAL_NAME", bpIdData.carrierName);

      const newCNPJs = bpIdData.companies.map((item) => ({
        CNPJ: item.cnpj,
        companyName: item.description,
      }));

      const existingCNPJs = values.attributes?.CNPJ || [];
      const merged = [...existingCNPJs];

      newCNPJs.forEach((newItem) => {
        if (!merged.some((existing) => existing.CNPJ === newItem.CNPJ)) {
          merged.push(newItem);
        }
      });

      setFieldValue("attributes.CNPJ", merged);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bpIdData]);

  return {
    values,
    errors,
    touched,
    setFieldValue,

    handleChange,
    handleBlur,
    handleProfileChange,
    handleAddCNPJ,
    handleSearchBPID,
    handleRemoveCNPJ,

    profileOptions,

    isCarrierProfile,
    isDriverBPIDValid,
    isGroupCNPJEmpty,
    isCNPJFieldsDisabled,
    isFormInvalid,
    isLoading,
  };
}
